// One dashboard widget tile: a hairline Section heading, the author tile Menu
// (Edit… / Duplicate / Remove — danger last, with a Popover confirm), and the
// widget body rendered from its OWN `metrics:query` read (per-tile queries:
// independent skeletons, cache reuse across boards, and draft widgets render
// before any save).

import { useMemo, useRef, useState, type ReactNode } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import { useMetric } from "../lib/dashboardData";
import {
  eligibilitySummary,
  formatPercent,
  formatUnits,
  previousPeriod,
  type MetricBucket,
  type MarkerDisplayNames,
  type TimeWindow,
} from "../lib/dashboardMetrics";
import {
  bucketForWindow,
  isWidgetContentEmpty,
  renderWidgetContent,
  type DashboardWidget,
  type RenderedWidgetContent,
  type WidgetTableRow,
} from "../lib/dashboardWidgets";
import { requestErrorDescription } from "../lib/errors";
import {
  Button,
  Chart,
  DataTable,
  EmptyState,
  Inline,
  InlineError,
  Menu,
  Popover,
  Section,
  Skeleton,
  Stack,
  StatCard,
  Text,
  type MenuItem,
} from "../ui";

export interface DashboardWidgetTileProps {
  agentId?: string;
  canAuthor: boolean;
  markerDisplayNames?: MarkerDisplayNames;
  onDuplicate?: (widget: DashboardWidget) => void;
  onEdit?: (widget: DashboardWidget) => void;
  onRemove?: (widget: DashboardWidget) => void;
  widget: DashboardWidget;
  window: TimeWindow;
}

// Chart drawing height for a tile of `height` grid rows: the tile is
// height*72px + (height-1)*16px gaps, minus the Section heading + rhythm (~88px).
function chartHeightForRows(rows: number): number {
  return Math.max(96, rows * 72 + (rows - 1) * 16 - 88);
}

export function DashboardWidgetTile({
  agentId,
  canAuthor,
  markerDisplayNames,
  onDuplicate,
  onEdit,
  onRemove,
  widget,
  window,
}: DashboardWidgetTileProps) {
  return (
    <WidgetFrame
      canAuthor={canAuthor}
      widget={widget}
      onDuplicate={onDuplicate}
      onEdit={onEdit}
      onRemove={onRemove}
    >
      <MetricWidgetBody
        agentId={agentId}
        markerDisplayNames={markerDisplayNames}
        statLabelHidden
        widget={widget}
        window={window}
      />
    </WidgetFrame>
  );
}

function WidgetFrame({
  canAuthor,
  children,
  widget,
  onDuplicate,
  onEdit,
  onRemove,
}: {
  canAuthor: boolean;
  children: ReactNode;
  widget: DashboardWidget;
  onDuplicate?: (widget: DashboardWidget) => void;
  onEdit?: (widget: DashboardWidget) => void;
  onRemove?: (widget: DashboardWidget) => void;
}) {
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useMemo(
    () => ({
      get current(): HTMLElement | null {
        return (
          menuWrapRef.current?.querySelector<HTMLElement>(
            'button[aria-haspopup="menu"]',
          ) ?? menuWrapRef.current
        );
      },
    }),
    [],
  );

  const items: MenuItem[] = [];
  if (onEdit) {
    items.push({
      icon: "pencil",
      id: "edit-widget",
      label: "Edit…",
      onSelect: () => {
        onEdit(widget);
      },
    });
  }
  if (onDuplicate) {
    items.push({
      icon: "copy",
      id: "duplicate-widget",
      label: "Duplicate",
      onSelect: () => {
        onDuplicate(widget);
      },
    });
  }
  if (onRemove) {
    items.push({
      danger: true,
      icon: "trash",
      id: "remove-widget",
      label: "Remove…",
      onSelect: () => {
        setIsRemoveOpen(true);
      },
    });
  }

  // Hover-revealed kebab, not a standing text button — the §2.12 repeated-tile
  // exception for dense widget grids.
  const actions =
    canAuthor && items.length > 0 ? (
      <div
        className="marker-popover-anchor marker-widget-actions"
        ref={menuWrapRef}
      >
        <Menu
          align="end"
          items={items}
          label={`${widget.title} actions`}
          trigger="kebab"
        />
        <Popover
          align="end"
          anchorRef={menuTriggerRef}
          aria-label={`Remove ${widget.title}`}
          isOpen={isRemoveOpen}
          onDismiss={() => {
            setIsRemoveOpen(false);
          }}
        >
          <Stack gap={4}>
            <Text>
              {`Remove the widget "${widget.title}" from this dashboard?`}
            </Text>
            <Inline>
              <Button
                variant="danger"
                onClick={() => {
                  setIsRemoveOpen(false);
                  onRemove?.(widget);
                }}
              >
                Remove widget
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsRemoveOpen(false);
                }}
              >
                Cancel
              </Button>
            </Inline>
          </Stack>
        </Popover>
      </div>
    ) : null;

  return (
    <Section actions={actions} hairline heading={widget.title}>
      {children}
    </Section>
  );
}

export function MetricWidgetBody({
  agentId,
  markerDisplayNames,
  statLabelHidden = false,
  widget,
  window,
}: {
  agentId?: string;
  markerDisplayNames?: MarkerDisplayNames;
  /** Framed tiles show the title in their Section heading — suppress the
   * StatCard's own label so it doesn't render twice. */
  statLabelHidden?: boolean;
  widget: DashboardWidget;
  window: TimeWindow;
}) {
  const query = widget.query;
  const bucket: MetricBucket =
    query.bucket ?? bucketForWindow(widget.visualization, window);
  const current = useMetric({
    agentId,
    metric: query.metric,
    bucket,
    filters: query.filters,
    group_by: query.group_by ?? [],
    window,
  });
  const previousWindow = useMemo(() => previousPeriod(window), [window]);
  const previous = useMetric({
    agentId,
    metric: query.metric,
    bucket,
    filters: query.filters,
    group_by: query.group_by ?? [],
    window: previousWindow,
    enabled:
      widget.visualization === "stat" &&
      widget.comparison === "previous_period",
  });

  if (current.isPending) {
    return (
      <Skeleton
        label={`Loading ${widget.title}`}
        rows={3}
        shape="detail-field"
      />
    );
  }
  if (current.isError) {
    return <InlineError>{requestErrorDescription(current.error)}</InlineError>;
  }

  const content = renderWidgetContent(
    widget,
    current.data,
    previous.data,
    markerDisplayNames,
  );
  const eligibility =
    query.metric === "marker_pass_rate"
      ? eligibilitySummary(current.data)
      : null;
  if (isWidgetContentEmpty(content) && eligibility === null) {
    return <EmptyState title="No data" />;
  }
  return (
    <Stack gap={2}>
      <WidgetContent
        content={content}
        heightRows={widget.layout.height}
        statLabelHidden={statLabelHidden}
        title={widget.title}
      />
      {eligibility ? (
        <Text variant="muted">
          {`Eligible denominator: ${formatUnits(eligibility.eligible)} · `}
          {`Not eligible: ${formatUnits(eligibility.notEligible)} (`}
          {eligibility.notEligibleRate === null
            ? "--"
            : formatPercent(eligibility.notEligibleRate)}
          {`)`}
        </Text>
      ) : null}
      {current.data.notice ? (
        <Text variant="muted">{current.data.notice}</Text>
      ) : null}
    </Stack>
  );
}

export function WidgetContent({
  content,
  heightRows,
  statLabelHidden = false,
  title,
}: {
  content: RenderedWidgetContent;
  heightRows: number;
  statLabelHidden?: boolean;
  title: string;
}) {
  const height = chartHeightForRows(heightRows);
  switch (content.kind) {
    case "stat":
      return (
        <StatCard
          delta={content.delta}
          label={statLabelHidden ? "" : title}
          value={content.value}
        />
      );
    case "chart":
      return (
        <Chart
          ariaLabel={title}
          data={content.data}
          height={height}
          valueFormatter={content.valueFormatter}
          variant={content.variant}
        />
      );
    case "stacked":
      return (
        <Chart
          ariaLabel={title}
          data={content.data}
          height={height}
          valueFormatter={content.valueFormatter}
          variant="stacked-bar"
        />
      );
    case "donut":
      return (
        <Chart
          ariaLabel={title}
          data={content.data}
          height={height}
          variant="donut"
        />
      );
    case "table": {
      const columns: Array<ColumnDef<WidgetTableRow>> =
        content.table.headers.map((header, index) => ({
          header,
          id: `column-${String(index)}`,
          cell: ({ row }) => row.original.cells[index] ?? "",
        }));
      return (
        <DataTable
          ariaLabel={title}
          columns={columns}
          data={content.table.rows}
          getRowId={(row) => row.key}
        />
      );
    }
  }
}
